import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { calculateLicensePoints, syncLicensePointsWithAuthorized } from '../utils/pointsCalculator';
import { initializePointsData } from './points/pointsDataService';

export const initializeUserData = async (userId: string, email: string) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      // Get user's licenses to calculate initial points
      const licensesResponse = await fetch(
        'https://services.shopiweb.fr/api/licenses/get_by_email/premium',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({ email }).toString()
        }
      );

      const licensesData = await licensesResponse.json();
      const premiumLicenses = licensesData.user?.nbAuthorizedLicenseType?.premium || 0;
      const initialPoints = await syncLicensePointsWithAuthorized(userId, premiumLicenses);

      const defaultUserData = {
        email,
        role: 'user',
        company: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      await setDoc(userRef, defaultUserData);
      await initializePointsData(userId, initialPoints);
      
      return {
        ...defaultUserData,
        points: {
          available: initialPoints,
          lastUpdate: new Date().toISOString()
        }
      };
    }
    
    return userDoc.data();
  } catch (error) {
    console.error('Error initializing user data:', error);
    throw error;
  }
};

export const updateUserProfile = async (userId: string, data: { company?: string }) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...data,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

export const getUserData = async (userId: string) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return userDoc.data();
    }
    return null;
  } catch (error) {
    console.error('Error getting user data:', error);
    throw error;
  }
};

export const getUserPoints = async (userId: string): Promise<number> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.points?.available || 0;
    }
    return 0;
  } catch (error) {
    console.error('Error getting user points:', error);
    return 0;
  }
};

export const updateUserPoints = async (userId: string, points: number): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      'points.available': points,
      'points.lastUpdate': new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating user points:', error);
    throw error;
  }
};